import type {
  Dispatch,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  SetStateAction,
  ComponentType,
  SVGProps,
} from "react";
import { useMemo, useState } from "react";
import {
  LuCircleCheck,
  LuCirclePlay,
  LuTarget,
  LuTrophy,
  LuUsers,
} from "react-icons/lu";
import AdminSection from "../../../shared/components/AdminSection";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import EmptyState from "../../../shared/components/EmptyState";
import EventActionButton from "../../../shared/components/EventActionButton";
import PageSizeSelector, {
  resolvePageSizeLimit,
  useStoredPageSize,
} from "../../../shared/components/PageSizeSelector";
import SearchInput from "../../../shared/components/SearchInput";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  formatEventDateRangeLabel,
  formatTimeLabel,
} from "../../../shared/utils/dateTime";
import { normalizeSearchText } from "../../../shared/utils/searchText";
import type { AuthUser } from "../../auth/shared/types/auth";
import type {
  ChallengeHistoryItem,
  ChallengeStatus,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";
import { challengeCategorySummary } from "../../challenges/utils/challengeLabels";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const TournamentTrophyIcon = LuTrophy as unknown as IconLike;
const TournamentPlayIcon = LuCirclePlay as unknown as IconLike;
const TournamentCheckIcon = LuCircleCheck as unknown as IconLike;
const TournamentUsersIcon = LuUsers as unknown as IconLike;
const ChallengeTargetIcon = LuTarget as unknown as IconLike;

type AdminHistoryPanelProps = {
  availableTypes: ChallengeType[];
  challengeRegistrationsOpenByEventId: Record<string, boolean>;
  challengeRegistrationsToggleLoadingByEventId: Record<string, boolean>;
  confirmDeleteChallengeError: string;
  confirmDeleteChallengeLoading: boolean;
  confirmDeleteChallengeTarget: ChallengeHistoryItem | null;
  dateFrom: string;
  dateTo: string;
  effectiveTypeFilters: ChallengeType[];
  filteredHistory: ChallengeHistoryItem[];
  historyLoading: boolean;
  historyOfficialFilter: "all" | "official" | "organizer";
  historyOrganizerUserIdFilter: string;
  historyTotal: number;
  isAdmin: boolean;
  isTournamentsRouteView: boolean;
  nameFilter: string;
  onConfirmDeleteChallenge: () => Promise<void>;
  onFinishChallenge?: (item: ChallengeHistoryItem) => Promise<void>;
  onOpenChallenge: (item: ChallengeHistoryItem) => void;
  onRequestDeleteChallenge: (item: ChallengeHistoryItem) => void;
  onStartChallenge?: (item: ChallengeHistoryItem) => Promise<void>;
  onToggleChallengeRegistrations: (
    item: ChallengeHistoryItem,
    event?: ReactMouseEvent | ReactKeyboardEvent,
  ) => Promise<void>;
  organizerUsers: AuthUser[];
  resolveChallengeEventId: (item: ChallengeHistoryItem) => string;
  selectedChallengeId: number | null;
  setConfirmDeleteChallengeTarget: Dispatch<
    SetStateAction<ChallengeHistoryItem | null>
  >;
  setDateFrom: Dispatch<SetStateAction<string>>;
  setDateTo: Dispatch<SetStateAction<string>>;
  setHistoryOfficialFilter: Dispatch<
    SetStateAction<"all" | "official" | "organizer">
  >;
  setHistoryOrganizerUserIdFilter: Dispatch<SetStateAction<string>>;
  setNameFilter: Dispatch<SetStateAction<string>>;
  finishingChallengeIds?: Record<number, boolean>;
  startingChallengeIds?: Record<number, boolean>;
  statusFilters: ChallengeStatus[];
  toggleStatus: (status: ChallengeStatus) => void;
  toggleType: (type: ChallengeType) => void;
};

function AdminHistoryIcon({
  name,
}: {
  name: "calendar" | "clock" | "participants" | "trash";
}) {
  if (name === "calendar") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 3v4" />
        <path d="M17 3v4" />
        <path d="M4 9h16" />
        <rect x="4" y="5" width="16" height="16" rx="2" />
      </svg>
    );
  }
  if (name === "clock") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  }
  if (name === "participants") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="9.5" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" />
      <path d="M5 5H3v2a4 4 0 0 0 4 4" />
      <path d="M19 5h2v2a4 4 0 0 1-4 4" />
    </svg>
  );
}

function ChallengeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2 4 14h7l-1 8 10-13h-7l1-7Z" />
    </svg>
  );
}

function eventActionLabel(label: string) {
  return label.replace(/^\+\s*/, "");
}

function splitEventDateRangeLabel(
  startValue?: string | null,
  endValue?: string | null,
) {
  const formatted = formatEventDateRangeLabel(startValue, endValue);
  const [dateLabel, timeLabel] = formatted.split(" - ");
  return {
    dateLabel: dateLabel || "-",
    timeLabel: timeLabel || "-",
  };
}

function firstCategoryStartAt(item: ChallengeHistoryItem) {
  return (item.registrationCategories || [])
    .map((category) => String(category.scheduledAt || "").trim())
    .filter(Boolean)
    .sort(
      (left, right) =>
        new Date(left).getTime() - new Date(right).getTime(),
    )[0] || null;
}

function splitTournamentDateRangeLabel(item: ChallengeHistoryItem) {
  const labels = splitEventDateRangeLabel(
    item.scheduledAt || item.createdAt,
    item.scheduledEndAt,
  );
  if (labels.timeLabel !== "-") return labels;
  const firstStartAt = firstCategoryStartAt(item);
  const firstStartTime = formatTimeLabel(firstStartAt);
  return {
    ...labels,
    timeLabel: firstStartTime === "-" ? labels.timeLabel : firstStartTime,
  };
}

function tournamentStatusLabel(status: ChallengeStatus) {
  if (status === "started") return "Em andamento";
  if (status === "finished") return "Finalizado";
  return "A iniciar";
}

function tournamentStatusClass(status: ChallengeStatus) {
  if (status === "started") return "is-started";
  if (status === "finished") return "is-finished";
  return "is-pending";
}

export function tournamentSlots(item: ChallengeHistoryItem) {
  const categorySlots = (item.registrationCategories || []).reduce(
    (total, category) => total + Number(category.slotLimit || 0),
    0,
  );
  const slots = Number(item.slotLimit || item.playersCount || categorySlots || 0);
  // Torneios encerrados podem ter categorias reconfiguradas após o
  // encerramento, deixando o total de vagas menor que os participantes já
  // registrados historicamente. Nesse caso o "de Y" não é confiável.
  const participantsCount = Number(item.participantsCount || 0);
  return slots >= participantsCount ? slots : 0;
}

function hasGeneratedTournamentBrackets(item: ChallengeHistoryItem) {
  return (
    Number(item.tournamentBracketsCount || 0) > 0 ||
    Number(item.tournamentBracketMatchesCount || 0) > 0
  );
}

function tournamentLocation(item: ChallengeHistoryItem) {
  return (
    String(item.address || "").trim() ||
    [item.city, item.state].filter(Boolean).join(" / ") ||
    "-"
  );
}

function tournamentMatchesPeriod(item: ChallengeHistoryItem, period: string) {
  if (period === "all") return true;
  const raw = item.scheduledAt || item.createdAt;
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) return false;
  const now = new Date();
  if (period === "upcoming") return date.getTime() >= now.getTime();
  if (period === "month") {
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    );
  }
  if (period === "last90") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 90);
    return date.getTime() >= cutoff.getTime();
  }
  return true;
}

function challengeTypeLabel(type: ChallengeType) {
  if (type === "super8") return "Super 8";
  if (type === "poco") return "Poço";
  if (type === "tournament") return "Torneio";
  return "Challenge";
}

export default function AdminHistoryPanel({
  availableTypes,
  challengeRegistrationsOpenByEventId,
  challengeRegistrationsToggleLoadingByEventId,
  confirmDeleteChallengeError,
  confirmDeleteChallengeLoading,
  confirmDeleteChallengeTarget,
  dateFrom,
  dateTo,
  effectiveTypeFilters,
  filteredHistory,
  historyLoading,
  historyOfficialFilter,
  historyOrganizerUserIdFilter,
  historyTotal,
  isAdmin,
  isTournamentsRouteView,
  nameFilter,
  onConfirmDeleteChallenge,
  onFinishChallenge,
  onOpenChallenge,
  onRequestDeleteChallenge,
  onStartChallenge,
  onToggleChallengeRegistrations,
  organizerUsers,
  resolveChallengeEventId,
  selectedChallengeId,
  setConfirmDeleteChallengeTarget,
  setDateFrom,
  setDateTo,
  setHistoryOfficialFilter,
  setHistoryOrganizerUserIdFilter,
  setNameFilter,
  finishingChallengeIds = {},
  startingChallengeIds = {},
  statusFilters,
  toggleStatus,
  toggleType,
}: AdminHistoryPanelProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;
  const listKindLabel = isTournamentsRouteView
    ? t.historyList.tournamentListKind
    : t.historyList.challengeListKind;
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [tournamentStatusFilter, setTournamentStatusFilter] = useState<
    "all" | ChallengeStatus
  >("all");
  const [tournamentPeriodFilter, setTournamentPeriodFilter] = useState("all");
  const [tournamentMoreFilter, setTournamentMoreFilter] = useState<
    "all" | "official" | "organizer"
  >("all");
  const [tournamentPage, setTournamentPage] = useState(1);
  const [challengeStatusFilter, setChallengeStatusFilter] = useState<
    "all" | ChallengeStatus
  >("all");
  const [challengePeriodFilter, setChallengePeriodFilter] = useState("all");
  const [challengeTypeFilter, setChallengeTypeFilter] = useState<
    "all" | Exclude<ChallengeType, "tournament">
  >("all");
  const [challengeMoreFilter, setChallengeMoreFilter] = useState<
    "all" | "official" | "organizer"
  >("all");
  const [challengePage, setChallengePage] = useState(1);
  const [
    confirmCloseRegistrationsTarget,
    setConfirmCloseRegistrationsTarget,
  ] = useState<ChallengeHistoryItem | null>(null);
  const [tournamentPageSize, setTournamentPageSize] = useStoredPageSize(
    "tnp_page_size_torneios",
  );
  const [challengePageSize, setChallengePageSize] = useStoredPageSize(
    "tnp_page_size_desafios",
  );
  const tournamentSummary = useMemo(() => {
    const totalParticipants = filteredHistory.reduce(
      (total, item) => total + Number(item.participantsCount || 0),
      0,
    );
    return {
      total: filteredHistory.length,
      started: filteredHistory.filter((item) => item.status === "started").length,
      finished: filteredHistory.filter((item) => item.status === "finished").length,
      totalParticipants,
    };
  }, [filteredHistory]);
  const challengeSummary = useMemo(() => {
    const totalParticipants = filteredHistory.reduce(
      (total, item) => total + Number(item.participantsCount || 0),
      0,
    );
    return {
      total: filteredHistory.length,
      started: filteredHistory.filter((item) => item.status === "started").length,
      finished: filteredHistory.filter((item) => item.status === "finished").length,
      totalParticipants,
    };
  }, [filteredHistory]);
  const visibleTournamentHistory = useMemo(() => {
    const search = normalizeSearchText(tournamentSearch);
    return filteredHistory.filter((item) => {
      const matchesSearch =
        !search ||
        [
          item.id,
          item.challengeName,
          item.groupName,
          item.organizerName,
          item.address,
          item.city,
          item.state,
        ]
          .map((value) => normalizeSearchText(value))
          .some((value) => value.includes(search));
      const matchesStatus =
        tournamentStatusFilter === "all" || item.status === tournamentStatusFilter;
      const matchesPeriod = tournamentMatchesPeriod(item, tournamentPeriodFilter);
      const matchesMore =
        tournamentMoreFilter === "all" ||
        (tournamentMoreFilter === "official"
          ? Boolean(item.isOfficial ?? true)
          : !Boolean(item.isOfficial ?? true));
      return matchesSearch && matchesStatus && matchesPeriod && matchesMore;
    });
  }, [
    filteredHistory,
    tournamentMoreFilter,
    tournamentPeriodFilter,
    tournamentSearch,
    tournamentStatusFilter,
  ]);
  const visibleChallengeHistory = useMemo(() => {
    const search = normalizeSearchText(nameFilter);
    return filteredHistory.filter((item) => {
      const matchesSearch =
        !search ||
        [
          item.id,
          item.challengeName,
          item.groupName,
          item.organizerName,
          item.address,
          item.city,
          item.state,
        ]
          .map((value) => normalizeSearchText(value))
          .some((value) => value.includes(search));
      const matchesStatus =
        challengeStatusFilter === "all" || item.status === challengeStatusFilter;
      const matchesPeriod = tournamentMatchesPeriod(item, challengePeriodFilter);
      const matchesType =
        challengeTypeFilter === "all" || item.challengeType === challengeTypeFilter;
      const matchesMore =
        challengeMoreFilter === "all" ||
        (challengeMoreFilter === "official"
          ? Boolean(item.isOfficial ?? true)
          : !Boolean(item.isOfficial ?? true));
      return (
        matchesSearch &&
        matchesStatus &&
        matchesPeriod &&
        matchesType &&
        matchesMore
      );
    });
  }, [
    challengeMoreFilter,
    challengePeriodFilter,
    challengeStatusFilter,
    challengeTypeFilter,
    filteredHistory,
    nameFilter,
  ]);
  const tournamentPageLimit = resolvePageSizeLimit(
    tournamentPageSize,
    visibleTournamentHistory.length,
  );
  const tournamentPageCount = Math.max(
    1,
    Math.ceil(visibleTournamentHistory.length / tournamentPageLimit),
  );
  const safeTournamentPage = Math.min(tournamentPage, tournamentPageCount);
  const pagedTournamentHistory =
    tournamentPageSize === "all"
      ? visibleTournamentHistory
      : visibleTournamentHistory.slice(
          (safeTournamentPage - 1) * tournamentPageLimit,
          safeTournamentPage * tournamentPageLimit,
        );
  const challengePageLimit = resolvePageSizeLimit(
    challengePageSize,
    visibleChallengeHistory.length,
  );
  const challengePageCount = Math.max(
    1,
    Math.ceil(visibleChallengeHistory.length / challengePageLimit),
  );
  const safeChallengePage = Math.min(challengePage, challengePageCount);
  const pagedChallengeHistory =
    challengePageSize === "all"
      ? visibleChallengeHistory
      : visibleChallengeHistory.slice(
          (safeChallengePage - 1) * challengePageLimit,
          safeChallengePage * challengePageLimit,
        );
  const visibleStart = isTournamentsRouteView
    ? visibleTournamentHistory.length > 0
      ? (safeTournamentPage - 1) * tournamentPageLimit + 1
      : 0
    : visibleChallengeHistory.length > 0
      ? (safeChallengePage - 1) * challengePageLimit + 1
      : 0;
  const visibleEnd = isTournamentsRouteView
    ? Math.min(
        safeTournamentPage * tournamentPageLimit,
        visibleTournamentHistory.length,
      )
    : Math.min(
        safeChallengePage * challengePageLimit,
        visibleChallengeHistory.length,
      );
  const confirmCloseRegistrationsEventId = confirmCloseRegistrationsTarget
    ? resolveChallengeEventId(confirmCloseRegistrationsTarget)
    : "";
  const confirmCloseRegistrationsLoading = Boolean(
    confirmCloseRegistrationsEventId &&
      challengeRegistrationsToggleLoadingByEventId[
        confirmCloseRegistrationsEventId
      ],
  );
  const closeRegistrationsConfirmModal = (
    <ConfirmModal
      open={Boolean(confirmCloseRegistrationsTarget)}
      title="Fechar inscrições"
      message={
        confirmCloseRegistrationsTarget
          ? `Fechar inscrições de "${confirmCloseRegistrationsTarget.challengeName || confirmCloseRegistrationsTarget.groupName}"?`
          : ""
      }
      confirmLabel="Fechar inscrições"
      cancelLabel={siteTexts.common.cancel}
      loading={confirmCloseRegistrationsLoading}
      loadingLabel="Fechando..."
      onCancel={() => {
        if (confirmCloseRegistrationsLoading) return;
        setConfirmCloseRegistrationsTarget(null);
      }}
      onConfirm={() => {
        if (!confirmCloseRegistrationsTarget) return;
        void onToggleChallengeRegistrations(
          confirmCloseRegistrationsTarget,
        ).then(() => setConfirmCloseRegistrationsTarget(null));
      }}
    />
  );

  if (isTournamentsRouteView) {
    const summaryCards = [
      {
        icon: <TournamentTrophyIcon />,
        label: "Total de torneios",
        tone: "trophy",
        value: tournamentSummary.total,
      },
      {
        icon: <TournamentPlayIcon />,
        label: "Em andamento",
        tone: "started",
        value: tournamentSummary.started,
      },
      {
        icon: <TournamentCheckIcon />,
        label: "Finalizados",
        tone: "finished",
        value: tournamentSummary.finished,
      },
      {
        icon: <TournamentUsersIcon />,
        label: "Total de participantes",
        tone: "participants",
        value: tournamentSummary.totalParticipants,
      },
    ];

    return (
      <>
        <ConfirmModal
          open={Boolean(confirmDeleteChallengeTarget)}
          title={t.actions.removeChallenge}
          message={
            confirmDeleteChallengeTarget
              ? `Remover "${confirmDeleteChallengeTarget.challengeName || confirmDeleteChallengeTarget.groupName}" (status: ${confirmDeleteChallengeTarget.status})?`
              : ""
          }
          confirmLabel={t.actions.removeChallenge}
          cancelLabel={siteTexts.common.cancel}
          loading={confirmDeleteChallengeLoading}
          error={confirmDeleteChallengeError}
          onCancel={() => {
            if (confirmDeleteChallengeLoading) return;
            setConfirmDeleteChallengeTarget(null);
          }}
          onConfirm={() => void onConfirmDeleteChallenge()}
        />
        {closeRegistrationsConfirmModal}

        <AdminSection className="admin-users-dashboard admin-tournaments-dashboard">
          <div className="admin-users-hero admin-tournaments-hero">
            <div className="admin-users-hero-copy">
              <span className="admin-users-hero-icon" aria-hidden="true">
                <TournamentTrophyIcon />
              </span>
              <div>
                <h2>Torneios</h2>
                <p>Gerencie seus torneios e acompanhe o desempenho.</p>
              </div>
            </div>
            <EventActionButton
              variant="primary"
              className="admin-history-create-button admin-tournaments-create-button"
              href="#/admin/criar-torneio"
              icon={<PlusIcon />}
              label={eventActionLabel(t.historyList.createTournament)}
            />
          </div>

          <div className="admin-users-summary-grid" aria-label="Resumo de torneios">
            {summaryCards.map((card) => (
              <div className="admin-users-summary-card" key={card.label}>
                <span
                  className={`admin-users-summary-icon admin-tournaments-summary-icon admin-tournaments-summary-icon--${card.tone}`}
                >
                  {card.icon}
                </span>
                <div>
                  <strong>{card.value}</strong>
                  <span>{card.label}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="admin-users-toolbar admin-tournaments-toolbar">
            <SearchInput
              className="admin-users-search"
              placeholder="Buscar torneio..."
              value={tournamentSearch}
              onChange={(event) => {
                setTournamentSearch(event.target.value);
                setTournamentPage(1);
              }}
              onClear={() => {
                setTournamentSearch("");
                setTournamentPage(1);
              }}
              aria-label="Buscar torneio"
            />
            <div className="admin-tournaments-filter-grid">
              <label>
                <span>Status</span>
                <select
                  value={tournamentStatusFilter}
                  onChange={(event) => {
                    setTournamentStatusFilter(
                      event.target.value as "all" | ChallengeStatus,
                    );
                    setTournamentPage(1);
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="pending">A iniciar</option>
                  <option value="started">Em andamento</option>
                  <option value="finished">Finalizado</option>
                </select>
              </label>
              <label>
                <span>Período</span>
                <select
                  value={tournamentPeriodFilter}
                  onChange={(event) => {
                    setTournamentPeriodFilter(event.target.value);
                    setTournamentPage(1);
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="upcoming">Próximos</option>
                  <option value="month">Este mês</option>
                  <option value="last90">Últimos 90 dias</option>
                </select>
              </label>
              <label>
                <span>Mais filtros</span>
                <select
                  value={tournamentMoreFilter}
                  onChange={(event) => {
                    setTournamentMoreFilter(
                      event.target.value as "all" | "official" | "organizer",
                    );
                    setTournamentPage(1);
                  }}
                >
                  <option value="all">Todos</option>
                  <option value="official">TNP Oficial</option>
                  <option value="organizer">Organizadores</option>
                </select>
              </label>
            </div>
          </div>

          {historyLoading && <Spinner label={t.statuses.loadingHistory} />}

          <div className="admin-tournaments-list">
            {pagedTournamentHistory.map((item) => {
              const rowEventId = resolveChallengeEventId(item);
              const registrationsOpen =
                challengeRegistrationsOpenByEventId[rowEventId];
              const loadingToggle =
                challengeRegistrationsToggleLoadingByEventId[rowEventId];
              const bracketsGenerated = hasGeneratedTournamentBrackets(item);
              const showRegistrationsToggle =
                item.status === "pending" &&
                (Boolean(registrationsOpen) || !bracketsGenerated);
              const slots = tournamentSlots(item);
              const { dateLabel, timeLabel } =
                splitTournamentDateRangeLabel(item);
              return (
                <article
                  className={`admin-tournament-card ${selectedChallengeId === item.id ? "selected" : ""}`}
                  key={item.id}
                >
                  <strong className="admin-tournament-id">#{item.id}</strong>
                  <div className="admin-tournament-media" aria-hidden="true">
                    {item.eventImageUrl || item.organizerLogoUrl ? (
                      <img
                        src={item.eventImageUrl || item.organizerLogoUrl || ""}
                        alt=""
                      />
                    ) : (
                      <TrophyIcon />
                    )}
                  </div>
                  <div className="admin-tournament-main">
                    <h3>{item.challengeName || item.groupName || "Torneio TNP"}</h3>
                    <p>
                      Organizador:{" "}
                      <strong>
                        {item.organizerName ||
                          (item.isOfficial ? "TNP Oficial" : "Não informado")}
                      </strong>
                    </p>
                  </div>
                  <div className="admin-tournament-badges">
                    <span
                      className={`admin-tournament-status ${tournamentStatusClass(
                        item.status,
                      )}`}
                    >
                      {tournamentStatusLabel(item.status)}
                    </span>
                    {(bracketsGenerated || registrationsOpen !== undefined) && (
                      <span
                        className={`admin-tournament-status ${
                          bracketsGenerated
                            ? "is-brackets-generated"
                            : registrationsOpen
                            ? "is-registration-open"
                            : "is-registration-closed"
                        }`}
                      >
                        {bracketsGenerated
                          ? "Chaves geradas"
                          : registrationsOpen
                          ? "Inscrições abertas"
                          : "Inscrições fechadas"}
                      </span>
                    )}
                  </div>
                  <div className="admin-tournament-meta">
                    <span>
                      <AdminHistoryIcon name="calendar" />
                      {dateLabel}
                    </span>
                    <span>
                      <AdminHistoryIcon name="clock" />
                      {timeLabel}
                    </span>
                  </div>
                  <div className="admin-tournament-location">
                    <small>Local</small>
                    <strong>{tournamentLocation(item)}</strong>
                  </div>
                  <div className="admin-tournament-participants">
                    <small>Participantes</small>
                    <strong>
                      {item.participantsCount}
                      {slots > 0 ? ` de ${slots}` : ""}
                    </strong>
                  </div>
                  <div className="admin-tournament-actions">
                    <button
                      className="admin-user-action admin-user-action--primary"
                      type="button"
                      onClick={() => onOpenChallenge(item)}
                    >
                      Editar
                    </button>
                    {item.status === "pending" && onStartChallenge && (
                      <button
                        className="admin-user-action"
                        type="button"
                        onClick={() => void onStartChallenge(item)}
                        disabled={Boolean(startingChallengeIds[item.id])}
                      >
                        {startingChallengeIds[item.id]
                          ? t.historyList.saving
                          : t.actions.startChallenge}
                      </button>
                    )}
                    {item.status === "started" && onFinishChallenge && (
                      <button
                        className="admin-user-action"
                        type="button"
                        onClick={() => void onFinishChallenge(item)}
                        disabled={Boolean(finishingChallengeIds[item.id])}
                      >
                        {finishingChallengeIds[item.id]
                          ? t.historyList.saving
                          : t.actions.finishChallenge}
                      </button>
                    )}
                    {showRegistrationsToggle && (
                      <button
                        className={`admin-user-action ${
                          registrationsOpen
                            ? "admin-tournament-action-close"
                            : "admin-tournament-action-open"
                        }`}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (registrationsOpen) {
                            setConfirmCloseRegistrationsTarget(item);
                            return;
                          }
                          void onToggleChallengeRegistrations(item, event);
                        }}
                        disabled={Boolean(loadingToggle)}
                      >
                        {loadingToggle
                          ? t.historyList.saving
                          : registrationsOpen
                            ? "Fechar inscrições"
                            : "Abrir inscrições"}
                      </button>
                    )}
                    <button
                      className="admin-user-action admin-user-action--danger"
                      type="button"
                      onClick={() => onRequestDeleteChallenge(item)}
                    >
                      Remover
                    </button>
                  </div>
                </article>
              );
            })}

            {!historyLoading && pagedTournamentHistory.length === 0 && (
              <EmptyState
                className="admin-empty-state"
                icon={<TrophyIcon />}
                title={t.historyList.emptyTournament}
                description="Ajuste os filtros ou crie um novo torneio."
              />
            )}
          </div>

          <div className="admin-history-list-footer admin-tournaments-footer">
            <span>
              {t.historyList.showing} {visibleStart} {t.historyList.rangeTo}{" "}
              {visibleEnd} {t.historyList.rangeOf}{" "}
              {visibleTournamentHistory.length} {listKindLabel}
            </span>
            <div
              className="admin-history-pagination"
              aria-label={t.historyList.paginationAria}
            >
              <button
                type="button"
                disabled={safeTournamentPage <= 1}
                onClick={() => setTournamentPage((page) => Math.max(1, page - 1))}
                aria-label={t.historyList.previousPageAria}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(3, tournamentPageCount) }).map(
                (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      type="button"
                      key={`tournament-page-${page}`}
                      className={safeTournamentPage === page ? "active" : ""}
                      aria-current={safeTournamentPage === page ? "page" : undefined}
                      onClick={() => setTournamentPage(page)}
                    >
                      {page}
                    </button>
                  );
                },
              )}
              <button
                type="button"
                disabled={safeTournamentPage >= tournamentPageCount}
                onClick={() =>
                  setTournamentPage((page) => Math.min(tournamentPageCount, page + 1))
                }
                aria-label={t.historyList.nextPageAria}
              >
                ›
              </button>
            </div>
            <PageSizeSelector
              className="admin-history-page-size"
              value={tournamentPageSize}
              onChange={(value) => {
                setTournamentPageSize(value);
                setTournamentPage(1);
              }}
            />
          </div>
        </AdminSection>
      </>
    );
  }

  const challengeSummaryCards = [
    {
      icon: <TournamentTrophyIcon />,
      label: "Total de desafios",
      tone: "trophy",
      value: challengeSummary.total,
    },
    {
      icon: <TournamentPlayIcon />,
      label: "Em andamento",
      tone: "started",
      value: challengeSummary.started,
    },
    {
      icon: <TournamentCheckIcon />,
      label: "Finalizados",
      tone: "finished",
      value: challengeSummary.finished,
    },
    {
      icon: <TournamentUsersIcon />,
      label: "Total de participantes",
      tone: "participants",
      value: challengeSummary.totalParticipants,
    },
  ];

  return (
    <>
      <ConfirmModal
        open={Boolean(confirmDeleteChallengeTarget)}
        title={t.actions.removeChallenge}
        message={
          confirmDeleteChallengeTarget
            ? `Remover "${confirmDeleteChallengeTarget.challengeName || confirmDeleteChallengeTarget.groupName}" (status: ${confirmDeleteChallengeTarget.status})?`
            : ""
        }
        confirmLabel={t.actions.removeChallenge}
        cancelLabel={siteTexts.common.cancel}
        loading={confirmDeleteChallengeLoading}
        error={confirmDeleteChallengeError}
        onCancel={() => {
          if (confirmDeleteChallengeLoading) return;
          setConfirmDeleteChallengeTarget(null);
        }}
        onConfirm={() => void onConfirmDeleteChallenge()}
      />
      {closeRegistrationsConfirmModal}

      <AdminSection className="admin-users-dashboard admin-tournaments-dashboard admin-challenges-modern-dashboard">
        <div className="admin-users-hero admin-tournaments-hero">
          <div className="admin-users-hero-copy">
            <span className="admin-users-hero-icon" aria-hidden="true">
              <ChallengeTargetIcon />
            </span>
            <div>
              <h2>Desafios</h2>
              <p>Gerencie e acompanhe todos os desafios da sua organização.</p>
            </div>
          </div>
          <EventActionButton
            variant="primary"
            className="admin-history-create-button admin-tournaments-create-button"
            href="#/admin/criar-desafio"
            icon={<PlusIcon />}
            label={eventActionLabel(t.historyList.createChallenge)}
          />
        </div>

        <div className="admin-users-summary-grid" aria-label="Resumo de desafios">
          {challengeSummaryCards.map((card) => (
            <div className="admin-users-summary-card" key={card.label}>
              <span
                className={`admin-users-summary-icon admin-challenges-summary-icon admin-tournaments-summary-icon admin-tournaments-summary-icon--${card.tone}`}
              >
                {card.icon}
              </span>
              <div>
                <strong>{card.value}</strong>
                <span>{card.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-users-toolbar admin-tournaments-toolbar">
          <SearchInput
            className="admin-users-search"
            placeholder="Buscar por nome do desafio..."
            value={nameFilter}
            onChange={(event) => {
              setNameFilter(event.target.value);
              setChallengePage(1);
            }}
            onClear={() => {
              setNameFilter("");
              setChallengePage(1);
            }}
            aria-label="Buscar por nome do desafio"
          />
          <div className="admin-tournaments-filter-grid admin-challenges-filter-grid">
            <label>
              <span>Status</span>
              <select
                value={challengeStatusFilter}
                onChange={(event) => {
                  setChallengeStatusFilter(
                    event.target.value as "all" | ChallengeStatus,
                  );
                  setChallengePage(1);
                }}
              >
                <option value="all">Todos</option>
                <option value="pending">A iniciar</option>
                <option value="started">Em andamento</option>
                <option value="finished">Finalizado</option>
              </select>
            </label>
            <label>
              <span>Período</span>
              <select
                value={challengePeriodFilter}
                onChange={(event) => {
                  setChallengePeriodFilter(event.target.value);
                  setChallengePage(1);
                }}
              >
                <option value="all">Todos</option>
                <option value="upcoming">Próximos</option>
                <option value="month">Este mês</option>
                <option value="last90">Últimos 90 dias</option>
              </select>
            </label>
            <label>
              <span>Tipo</span>
              <select
                value={challengeTypeFilter}
                onChange={(event) => {
                  setChallengeTypeFilter(
                    event.target.value as "all" | Exclude<ChallengeType, "tournament">,
                  );
                  setChallengePage(1);
                }}
              >
                <option value="all">Todos</option>
                {availableTypes
                  .filter((type) => type !== "tournament")
                  .map((type) => (
                    <option value={type} key={`challenge-type-${type}`}>
                      {challengeTypeLabel(type)}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <span>Mais filtros</span>
              <select
                value={challengeMoreFilter}
                onChange={(event) => {
                  const nextValue = event.target.value as
                    | "all"
                    | "official"
                    | "organizer";
                  setChallengeMoreFilter(nextValue);
                  setHistoryOfficialFilter(nextValue);
                  setChallengePage(1);
                }}
              >
                <option value="all">Todos</option>
                <option value="official">TNP Oficial</option>
                <option value="organizer">Organizadores</option>
              </select>
            </label>
          </div>

          {isAdmin && challengeMoreFilter === "organizer" && (
            <div className="admin-tournaments-filter-grid admin-challenges-organizer-filter">
              <label>
                <span>Organizador</span>
                <select
                  value={historyOrganizerUserIdFilter}
                  onChange={(event) => {
                    setHistoryOrganizerUserIdFilter(event.target.value);
                    setChallengePage(1);
                  }}
                >
                  <option value="">{t.historyList.all}</option>
                  {organizerUsers.map((user) => (
                    <option
                      key={`organizer-filter-${user.id}`}
                      value={String(user.id)}
                    >
                      {user.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        {historyLoading && <Spinner label={t.statuses.loadingHistory} />}

        <div className="admin-tournaments-list admin-challenges-modern-list">
          {pagedChallengeHistory.map((item) => {
            const rowEventId = resolveChallengeEventId(item);
            const registrationsOpen =
              challengeRegistrationsOpenByEventId[rowEventId];
            const bracketsGenerated = hasGeneratedTournamentBrackets(item);
            const slots = tournamentSlots(item);
            const { dateLabel, timeLabel } = splitEventDateRangeLabel(
              item.scheduledAt || item.createdAt,
              item.scheduledEndAt,
            );
            return (
              <article
                className={`admin-tournament-card admin-challenge-modern-card ${
                  selectedChallengeId === item.id ? "selected" : ""
                }`}
                key={item.id}
              >
                <strong className="admin-tournament-id">#{item.id}</strong>
                <div className="admin-tournament-media" aria-hidden="true">
                  {item.eventImageUrl || item.organizerLogoUrl ? (
                    <img
                      src={item.eventImageUrl || item.organizerLogoUrl || ""}
                      alt=""
                    />
                  ) : (
                    <ChallengeIcon />
                  )}
                </div>
                <div className="admin-tournament-main">
                  <h3>{item.challengeName || item.groupName || "Desafio TNP"}</h3>
                  <p>
                    Organizador:{" "}
                    <strong>
                      {item.organizerName ||
                        (item.isOfficial ? "TNP Oficial" : "Não informado")}
                    </strong>
                  </p>
                  <p className="admin-challenge-modern-meta">
                    Categoria: {challengeCategorySummary(item)} • Ranking:{" "}
                    {Boolean(item.includeInRanking ?? true)
                      ? createTexts.yes
                      : createTexts.no}
                  </p>
                </div>
                <div className="admin-tournament-badges">
                  <span className="admin-tournament-status is-challenge-type">
                    {challengeTypeLabel(item.challengeType)}
                  </span>
                  <span
                    className={`admin-tournament-status ${tournamentStatusClass(
                      item.status,
                    )}`}
                  >
                    {tournamentStatusLabel(item.status)}
                  </span>
                  {(bracketsGenerated || registrationsOpen !== undefined) && (
                    <span
                      className={`admin-tournament-status ${
                        bracketsGenerated
                          ? "is-brackets-generated"
                          : registrationsOpen
                          ? "is-registration-open"
                          : "is-registration-closed"
                      }`}
                    >
                      {bracketsGenerated
                        ? "Chaves geradas"
                        : registrationsOpen
                        ? "Inscrições abertas"
                        : "Inscrições fechadas"}
                    </span>
                  )}
                </div>
                <div className="admin-tournament-meta">
                  <span>
                    <AdminHistoryIcon name="calendar" />
                    {dateLabel}
                  </span>
                  <span>
                    <AdminHistoryIcon name="clock" />
                    {timeLabel}
                  </span>
                </div>
                <div className="admin-tournament-location">
                  <small>Local</small>
                  <strong>{tournamentLocation(item)}</strong>
                </div>
                <div className="admin-tournament-participants">
                  <small>Participantes</small>
                  <strong>
                    {item.participantsCount}
                    {slots > 0 ? ` de ${slots}` : ""}
                  </strong>
                </div>
                <div className="admin-tournament-actions">
                  <button
                    className="admin-user-action admin-user-action--primary"
                    type="button"
                    onClick={() => onOpenChallenge(item)}
                  >
                    Editar
                  </button>
                  <button
                    className="admin-user-action admin-user-action--danger"
                    type="button"
                    onClick={() => onRequestDeleteChallenge(item)}
                  >
                    Remover
                  </button>
                </div>
              </article>
            );
          })}

          {!historyLoading && pagedChallengeHistory.length === 0 && (
            <EmptyState
              className="admin-empty-state"
              icon={<ChallengeIcon />}
              title={t.historyList.emptyChallenge}
              description="Ajuste os filtros ou crie um novo desafio."
            />
          )}
        </div>

        <div className="admin-history-list-footer admin-tournaments-footer">
          <span>
            {t.historyList.showing} {visibleStart} {t.historyList.rangeTo}{" "}
            {visibleEnd} {t.historyList.rangeOf} {visibleChallengeHistory.length}{" "}
            {listKindLabel}
          </span>
          <div
            className="admin-history-pagination"
            aria-label={t.historyList.paginationAria}
          >
            <button
              type="button"
              disabled={safeChallengePage <= 1}
              onClick={() => setChallengePage((page) => Math.max(1, page - 1))}
              aria-label={t.historyList.previousPageAria}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(3, challengePageCount) }).map(
              (_, index) => {
                const page = index + 1;
                return (
                  <button
                    type="button"
                    key={`challenge-page-${page}`}
                    className={safeChallengePage === page ? "active" : ""}
                    aria-current={safeChallengePage === page ? "page" : undefined}
                    onClick={() => setChallengePage(page)}
                  >
                    {page}
                  </button>
                );
              },
            )}
            <button
              type="button"
              disabled={safeChallengePage >= challengePageCount}
              onClick={() =>
                setChallengePage((page) => Math.min(challengePageCount, page + 1))
              }
              aria-label={t.historyList.nextPageAria}
            >
              ›
            </button>
          </div>
          <PageSizeSelector
            className="admin-history-page-size"
            value={challengePageSize}
            onChange={(value) => {
              setChallengePageSize(value);
              setChallengePage(1);
            }}
          />
        </div>
      </AdminSection>
    </>
  );

}
